import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { openInExternalBrowser, signInWithGoogle, signInWithKakao } from '../services/socialAuth';
import { authApi } from '../shared/auth';

/**
 * 로그인 모달 컴포넌트
 * NavigationBar에서 모달로 호출
 */
const LoginModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('social');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [showExternalBrowserPrompt, setShowExternalBrowserPrompt] = useState(false);

  // ESC 키로 닫기
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // 모달 초기화
  useEffect(() => {
    if (isOpen) {
      setError('');
      setShowExternalBrowserPrompt(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authApi.login(email, password);
      onClose();
      window.location.reload();
    } catch (err) {
      if (err.message === 'Account pending approval') {
        setError(t('auth.login.errorPending'));
      } else if (err.message === 'Account rejected') {
        setError(t('auth.login.errorRejected'));
      } else {
        setError(t('auth.login.error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setError('');
    setShowExternalBrowserPrompt(false);
    setSocialLoading(provider);

    try {
      // 1. Get social user info from SDK
      let socialUser;
      if (provider === 'google') {
        socialUser = await signInWithGoogle();
      } else if (provider === 'kakao') {
        socialUser = await signInWithKakao();
      } else {
        throw new Error('Unknown provider');
      }

      // 2. Send to backend for authentication
      await authApi.socialLogin(provider, socialUser);
      onClose();
      window.location.reload();
    } catch (err) {
      console.error('Social login error:', err);
      if (err.message === 'Account rejected') {
        setError(t('auth.login.errorRejected'));
      } else if (err.error === 'popup_closed_by_user') {
        // 사용자가 팝업 닫음 - 에러 표시 안함
      } else if (err.type === 'webview_blocked') {
        setShowExternalBrowserPrompt(true);
      } else {
        setError(t('auth.social.error') + ` (${err.message || err.error || 'Unknown error'})`);
      }
    } finally {
      setSocialLoading(null);
    }
  };

  const handleOpenInExternalBrowser = () => {
    openInExternalBrowser(window.location.href);
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      alert('URL이 복사되었습니다.\nChrome 또는 Safari에서 붙여넣기 하세요.');
    });
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 디버깅
  console.log('LoginModal render, isOpen:', isOpen);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-[420px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-fadeIn">
        {/* 닫기 버튼 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-8 pt-12">
          <h2 className="text-2xl font-semibold text-center mb-2">{t('auth.login.title')}</h2>
          <p className="text-center text-gray-500 mb-6 text-sm">{t('auth.login.subtitle')}</p>

          {error && (
            <div className="bg-red-100 text-red-600 py-3 px-4 rounded-lg mb-4 text-sm text-center">
              {error}
            </div>
          )}

          {/* 외부 브라우저 안내 */}
          {showExternalBrowserPrompt && (
            <div className="flex gap-3 p-4 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-300 rounded-xl mb-4">
              <div className="text-2xl flex-shrink-0">🌐</div>
              <div className="flex-1">
                <strong className="block text-blue-700 mb-1 text-sm">외부 브라우저에서 로그인해주세요</strong>
                <p className="text-blue-700 text-xs mb-2 leading-relaxed">
                  Google 로그인은 보안상 인앱 브라우저에서 지원되지 않습니다.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleOpenInExternalBrowser}
                    className="flex-1 py-2 px-3 bg-blue-500 text-white border-none rounded-lg text-xs font-semibold cursor-pointer transition-colors hover:bg-blue-600"
                  >
                    외부 브라우저로 열기
                  </button>
                  <button
                    onClick={handleCopyUrl}
                    className="py-2 px-3 bg-white text-blue-500 border border-blue-500 rounded-lg text-xs font-medium cursor-pointer transition-colors hover:bg-blue-50"
                  >
                    URL 복사
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 로그인 탭 */}
          <div className="flex mb-5 border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              className={`flex-1 py-3 px-4 border-none text-sm font-medium cursor-pointer transition-all ${
                activeTab === 'social'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              onClick={() => setActiveTab('social')}
            >
              간편 로그인
            </button>
            <button
              type="button"
              className={`flex-1 py-3 px-4 border-none text-sm font-medium cursor-pointer transition-all border-l border-gray-200 ${
                activeTab === 'email'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              onClick={() => setActiveTab('email')}
            >
              이메일 로그인
            </button>
          </div>

          {/* 소셜 로그인 탭 */}
          {activeTab === 'social' && (
            <div className="flex flex-col gap-3">
              <button
                type="button"
                className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-lg text-sm font-medium cursor-pointer transition-all border border-transparent bg-[#FEE500] text-black hover:bg-[#F5DC00] disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={() => handleSocialLogin('kakao')}
                disabled={socialLoading}
              >
                <svg className="w-5 h-5 text-black" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 3C6.48 3 2 6.48 2 10.8c0 2.76 1.84 5.18 4.59 6.55-.2.76-.73 2.75-.84 3.18-.14.54.2.53.42.39.17-.11 2.72-1.85 3.83-2.6.65.09 1.32.14 2 .14 5.52 0 10-3.48 10-7.8S17.52 3 12 3z"/>
                </svg>
                {socialLoading === 'kakao' ? t('auth.social.loading') : t('auth.social.kakao')}
              </button>

              <button
                type="button"
                className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-lg text-sm font-medium cursor-pointer transition-all border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={() => handleSocialLogin('google')}
                disabled={socialLoading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {socialLoading === 'google' ? t('auth.social.loading') : t('auth.social.google')}
              </button>
            </div>
          )}

          {/* 이메일 로그인 탭 */}
          {activeTab === 'email' && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="modal-email" className="font-medium text-sm text-gray-900">{t('auth.email')}</label>
                <input
                  type="email"
                  id="modal-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  autoComplete="email"
                  className="py-3 px-4 border border-gray-200 rounded-lg text-sm transition-all focus:outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/5 placeholder:text-gray-400"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="modal-password" className="font-medium text-sm text-gray-900">{t('auth.password')}</label>
                <input
                  type="password"
                  id="modal-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                  autoComplete="current-password"
                  className="py-3 px-4 border border-gray-200 rounded-lg text-sm transition-all focus:outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/5 placeholder:text-gray-400"
                />
              </div>

              <button
                type="submit"
                className="mt-1 py-3 px-6 bg-gray-900 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-gray-800 active:scale-[0.98] disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? t('auth.login.loading') : t('auth.login.button')}
              </button>

              <p className="text-center text-gray-400 text-xs">
                관리자 계정만 이메일 로그인이 가능합니다
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
